import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Moon, 
  Sun, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  ArrowLeft,
  ArrowRight,
  Phone,
  Calendar,
  TrendingUp,
  Info,
  Download,
  Share2,
  BarChart3
} from 'lucide-react';

/**
 * SleepAssessment Component
 * Pittsburgh Sleep Quality Index (PSQI) - 19 questions
 * 
 * Based on GitHub patterns from:
 * - Parivarthan project structure (App.js routing)
 * - Existing assessment components (DepressionScreening, AnxietyAssessment)
 * - PSQI standardized clinical assessment
 * - Responsive design for tablets (768px - 1024px)
 * 
 * Features:
 * - Multi-step form with progress tracking
 * - PSQI scoring algorithm (0-21 scale)
 * - Component scores for 7 sleep quality dimensions
 * - Tablet-optimized responsive layout
 * - Results with clinical interpretation
 * - Save/share functionality
 * - Integration with Parivarthan routing
 */

const SleepAssessment = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [assessmentData, setAssessmentData] = useState({
    // Component 1: Subjective Sleep Quality
    sleepQuality: '',
    
    // Component 2: Sleep Latency
    sleepLatencyMinutes: '',
    sleepLatencyTrouble: '',
    
    // Component 3: Sleep Duration
    hoursOfSleep: '',
    
    // Component 4: Sleep Efficiency
    bedTime: '',
    wakeTime: '',
    
    // Component 5: Sleep Disturbances (9 items)
    disturbances: {
      wakeUpNight: '',
      bathroom: '',
      breathe: '',
      coughSnore: '',
      coldFeel: '',
      hotFeel: '',
      badDreams: '',
      pain: '',
      otherReasons: ''
    },
    
    // Component 6: Sleep Medication
    sleepMedication: '',
    
    // Component 7: Daytime Dysfunction
    troubleStayingAwake: '',
    enthusiasmProblems: ''
  });

  const [scores, setScores] = useState({
    component1: 0, // Subjective sleep quality
    component2: 0, // Sleep latency
    component3: 0, // Sleep duration
    component4: 0, // Sleep efficiency
    component5: 0, // Sleep disturbances
    component6: 0, // Use of sleep medication
    component7: 0, // Daytime dysfunction
    global: 0
  });

  // Questions organized by component
  const questions = [
    {
      step: 0,
      component: 'intro',
      title: 'Sleep Quality Assessment',
      subtitle: 'Pittsburgh Sleep Quality Index (PSQI)',
      description: 'This assessment evaluates your sleep quality over the past month. It takes about 10-15 minutes to complete and covers various aspects of your sleep patterns.',
      info: [
        '19 self-rated questions',
        'Assesses sleep quality over the past month',
        'Clinically validated assessment',
        'Results provided immediately'
      ]
    },
    {
      step: 1,
      component: 'bedtime',
      title: 'Sleep Schedule',
      question: 'During the past month, when have you usually gone to bed at night?',
      type: 'time',
      field: 'bedTime',
      placeholder: 'e.g., 10:30 PM'
    },
    {
      step: 2,
      component: 'sleep-latency',
      title: 'Sleep Onset',
      question: 'During the past month, how long (in minutes) has it usually taken you to fall asleep each night?',
      type: 'number',
      field: 'sleepLatencyMinutes',
      placeholder: 'Minutes',
      min: 0,
      max: 240
    },
    {
      step: 3,
      component: 'wake-time',
      title: 'Wake Time',
      question: 'During the past month, when have you usually gotten up in the morning?',
      type: 'time',
      field: 'wakeTime',
      placeholder: 'e.g., 6:30 AM'
    },
    {
      step: 4,
      component: 'sleep-duration',
      title: 'Sleep Duration',
      question: 'During the past month, how many hours of actual sleep did you get at night? (This may be different than the number of hours you spent in bed)',
      type: 'number',
      field: 'hoursOfSleep',
      placeholder: 'Hours',
      min: 0,
      max: 24,
      step: 0.5
    },
    {
      step: 5,
      component: 'disturbances-1',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you cannot get to sleep within 30 minutes?',
      type: 'likert',
      field: ['disturbances', 'sleepLatencyTrouble'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 6,
      component: 'disturbances-2',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you wake up in the middle of the night or early morning?',
      type: 'likert',
      field: ['disturbances', 'wakeUpNight'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 7,
      component: 'disturbances-3',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you have to get up to use the bathroom?',
      type: 'likert',
      field: ['disturbances', 'bathroom'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 8,
      component: 'disturbances-4',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you cannot breathe comfortably?',
      type: 'likert',
      field: ['disturbances', 'breathe'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 9,
      component: 'disturbances-5',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you cough or snore loudly?',
      type: 'likert',
      field: ['disturbances', 'coughSnore'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 10,
      component: 'disturbances-6',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you feel too cold?',
      type: 'likert',
      field: ['disturbances', 'coldFeel'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 11,
      component: 'disturbances-7',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you feel too hot?',
      type: 'likert',
      field: ['disturbances', 'hotFeel'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 12,
      component: 'disturbances-8',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you have bad dreams?',
      type: 'likert',
      field: ['disturbances', 'badDreams'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 13,
      component: 'disturbances-9',
      title: 'Sleep Disturbances',
      question: 'During the past month, how often have you had trouble sleeping because you have pain?',
      type: 'likert',
      field: ['disturbances', 'pain'],
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 14,
      component: 'quality',
      title: 'Overall Sleep Quality',
      question: 'During the past month, how would you rate your sleep quality overall?',
      type: 'likert',
      field: 'sleepQuality',
      options: [
        { value: '0', label: 'Very good' },
        { value: '1', label: 'Fairly good' },
        { value: '2', label: 'Fairly bad' },
        { value: '3', label: 'Very bad' }
      ]
    },
    {
      step: 15,
      component: 'medication',
      title: 'Sleep Medication',
      question: 'During the past month, how often have you taken medicine (prescribed or "over the counter") to help you sleep?',
      type: 'likert',
      field: 'sleepMedication',
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 16,
      component: 'daytime-1',
      title: 'Daytime Function',
      question: 'During the past month, how often have you had trouble staying awake while driving, eating meals, or engaging in social activity?',
      type: 'likert',
      field: 'troubleStayingAwake',
      options: [
        { value: '0', label: 'Not during the past month' },
        { value: '1', label: 'Less than once a week' },
        { value: '2', label: 'Once or twice a week' },
        { value: '3', label: 'Three or more times a week' }
      ]
    },
    {
      step: 17,
      component: 'daytime-2',
      title: 'Daytime Function',
      question: 'During the past month, how much of a problem has it been for you to keep up enough enthusiasm to get things done?',
      type: 'likert',
      field: 'enthusiasmProblems',
      options: [
        { value: '0', label: 'No problem at all' },
        { value: '1', label: 'Only a very slight problem' },
        { value: '2', label: 'Somewhat of a problem' },
        { value: '3', label: 'A very big problem' }
      ]
    }
  ];

  // Calculate PSQI scores
  const calculateScores = () => {
    const data = assessmentData;
    let newScores = { ...scores };

    // Component 1: Subjective Sleep Quality
    newScores.component1 = parseInt(data.sleepQuality) || 0;

    // Component 2: Sleep Latency
    const latencyMinutes = parseInt(data.sleepLatencyMinutes) || 0;
    let latencyScore = 0;
    if (latencyMinutes <= 15) latencyScore = 0;
    else if (latencyMinutes <= 30) latencyScore = 1;
    else if (latencyMinutes <= 60) latencyScore = 2;
    else latencyScore = 3;

    const latencyTrouble = parseInt(data.disturbances.sleepLatencyTrouble) || 0;
    const component2Sum = latencyScore + latencyTrouble;
    
    if (component2Sum === 0) newScores.component2 = 0;
    else if (component2Sum <= 2) newScores.component2 = 1;
    else if (component2Sum <= 4) newScores.component2 = 2;
    else newScores.component2 = 3;

    // Component 3: Sleep Duration
    const hours = parseFloat(data.hoursOfSleep) || 0;
    if (hours > 7) newScores.component3 = 0;
    else if (hours >= 6) newScores.component3 = 1;
    else if (hours >= 5) newScores.component3 = 2;
    else newScores.component3 = 3;

    // Component 4: Sleep Efficiency
    const bedTimeStr = data.bedTime;
    const wakeTimeStr = data.wakeTime;
    
    if (bedTimeStr && wakeTimeStr && hours > 0) {
      // Calculate time in bed (simplified calculation)
      const bedHour = parseInt(bedTimeStr.split(':')[0]);
      const wakeHour = parseInt(wakeTimeStr.split(':')[0]);
      let timeInBed = wakeHour - bedHour;
      if (timeInBed < 0) timeInBed += 24;
      
      const efficiency = (hours / timeInBed) * 100;
      
      if (efficiency >= 85) newScores.component4 = 0;
      else if (efficiency >= 75) newScores.component4 = 1;
      else if (efficiency >= 65) newScores.component4 = 2;
      else newScores.component4 = 3;
    }

    // Component 5: Sleep Disturbances
    const disturbanceItems = [
      data.disturbances.wakeUpNight,
      data.disturbances.bathroom,
      data.disturbances.breathe,
      data.disturbances.coughSnore,
      data.disturbances.coldFeel,
      data.disturbances.hotFeel,
      data.disturbances.badDreams,
      data.disturbances.pain,
      data.disturbances.otherReasons
    ];
    
    const disturbanceSum = disturbanceItems.reduce((sum, item) => 
      sum + (parseInt(item) || 0), 0
    );
    
    if (disturbanceSum === 0) newScores.component5 = 0;
    else if (disturbanceSum <= 9) newScores.component5 = 1;
    else if (disturbanceSum <= 18) newScores.component5 = 2;
    else newScores.component5 = 3;

    // Component 6: Sleep Medication
    newScores.component6 = parseInt(data.sleepMedication) || 0;

    // Component 7: Daytime Dysfunction
    const daytimeSum = (parseInt(data.troubleStayingAwake) || 0) + 
                      (parseInt(data.enthusiasmProblems) || 0);
    
    if (daytimeSum === 0) newScores.component7 = 0;
    else if (daytimeSum <= 2) newScores.component7 = 1;
    else if (daytimeSum <= 4) newScores.component7 = 2;
    else newScores.component7 = 3;

    // Global PSQI Score (0-21)
    newScores.global = newScores.component1 + newScores.component2 + 
                      newScores.component3 + newScores.component4 + 
                      newScores.component5 + newScores.component6 + 
                      newScores.component7;

    setScores(newScores);
    return newScores;
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    if (Array.isArray(field)) {
      // Nested field (e.g., disturbances)
      setAssessmentData(prev => ({
        ...prev,
        [field[0]]: {
          ...prev[field[0]],
          [field[1]]: value
        }
      }));
    } else {
      setAssessmentData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    } else {
      const calculatedScores = calculateScores();
      setShowResults(true);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setShowResults(false);
    setAssessmentData({
      sleepQuality: '',
      sleepLatencyMinutes: '',
      hoursOfSleep: '',
      bedTime: '',
      wakeTime: '',
      disturbances: {
        sleepLatencyTrouble: '',
        wakeUpNight: '',
        bathroom: '',
        breathe: '',
        coughSnore: '',
        coldFeel: '',
        hotFeel: '',
        badDreams: '',
        pain: '',
        otherReasons: ''
      },
      sleepMedication: '',
      troubleStayingAwake: '',
      enthusiasmProblems: ''
    });
    window.scrollTo(0, 0);
  };

  // Get severity interpretation
  const getSeverityInfo = (score) => {
    if (score <= 5) {
      return {
        level: 'Good Sleep Quality',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: CheckCircle,
        description: 'Your sleep quality is good. You\'re getting adequate, restful sleep.',
        recommendations: [
          'Maintain your current sleep schedule',
          'Continue healthy sleep habits',
          'Practice good sleep hygiene',
          'Regular exercise and balanced diet'
        ]
      };
    } else if (score <= 10) {
      return {
        level: 'Fair Sleep Quality',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: AlertCircle,
        description: 'Your sleep quality could be improved. Some sleep disturbances are present.',
        recommendations: [
          'Establish a consistent sleep schedule',
          'Create a relaxing bedtime routine',
          'Limit screen time before bed',
          'Consider tracking sleep patterns',
          'Avoid caffeine and alcohol before sleep'
        ]
      };
    } else if (score <= 15) {
      return {
        level: 'Poor Sleep Quality',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: AlertCircle,
        description: 'Your sleep quality is significantly impaired. This may be affecting your daily functioning.',
        recommendations: [
          'Consult with a healthcare provider',
          'Keep a detailed sleep diary',
          'Review medications that may affect sleep',
          'Consider cognitive behavioral therapy for insomnia (CBT-I)',
          'Evaluate your sleep environment',
          'Rule out sleep disorders'
        ]
      };
    } else {
      return {
        level: 'Very Poor Sleep Quality',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: XCircle,
        description: 'Your sleep quality is severely impaired. Immediate professional evaluation is recommended.',
        recommendations: [
          'Schedule an appointment with a sleep specialist immediately',
          'Discuss with your primary care physician',
          'Consider sleep study evaluation',
          'Address underlying medical conditions',
          'Professional treatment may be necessary',
          'Avoid self-medication'
        ]
      };
    }
  };

  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const currentQuestion = questions[currentStep];
    
    if (currentQuestion.component === 'intro') return true;
    
    const field = currentQuestion.field;
    
    if (Array.isArray(field)) {
      return assessmentData[field[0]][field[1]] !== '';
    }
    
    return assessmentData[field] !== '';
  };

  // Progress percentage
  const progressPercentage = ((currentStep + 1) / questions.length) * 100;

  // Render Results View
  if (showResults) {
    const severityInfo = getSeverityInfo(scores.global);
    const SeverityIcon = severityInfo.icon;

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-full mb-4">
              <Moon className="w-8 h-8 text-indigo-600" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Your Sleep Assessment Results
            </h1>
            <p className="text-gray-600">Pittsburgh Sleep Quality Index (PSQI)</p>
          </div>

          {/* Global Score Card */}
          <div className={`${severityInfo.bgColor} ${severityInfo.borderColor} border-2 rounded-2xl p-6 md:p-8 mb-6 shadow-lg`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Global PSQI Score: {scores.global}
                </h2>
                <div className={`flex items-center gap-2 ${severityInfo.color} text-lg font-semibold`}>
                  <SeverityIcon className="w-6 h-6" />
                  {severityInfo.level}
                </div>
              </div>
              <div className="text-right">
                <div className="text-5xl md:text-6xl font-bold text-gray-900">
                  {scores.global}
                </div>
                <div className="text-sm text-gray-600">out of 21</div>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-3 mb-6">
              <div 
                className={`h-3 rounded-full transition-all duration-500 ${
                  scores.global <= 5 ? 'bg-green-500' :
                  scores.global <= 10 ? 'bg-yellow-500' :
                  scores.global <= 15 ? 'bg-orange-500' : 'bg-red-500'
                }`}
                style={{ width: `${(scores.global / 21) * 100}%` }}
              />
            </div>

            <p className="text-gray-700 text-base md:text-lg">
              {severityInfo.description}
            </p>

            {scores.global > 5 && (
              <div className="mt-4 p-4 bg-white rounded-lg">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700">
                    A global PSQI score greater than 5 indicates poor sleep quality. 
                    Consider discussing these results with a healthcare provider.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Component Scores Breakdown */}
          <div className="bg-white rounded-2xl p-6 md:p-8 mb-6 shadow-lg">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
              Sleep Components Analysis
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { name: 'Sleep Quality', score: scores.component1, icon: '⭐' },
                { name: 'Sleep Latency', score: scores.component2, icon: '⏱️' },
                { name: 'Sleep Duration', score: scores.component3, icon: '🕐' },
                { name: 'Sleep Efficiency', score: scores.component4, icon: '📊' },
                { name: 'Sleep Disturbances', score: scores.component5, icon: '🌙' },
                { name: 'Sleep Medication', score: scores.component6, icon: '💊' },
                { name: 'Daytime Dysfunction', score: scores.component7, icon: '☀️' }
              ].map((component, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{component.icon}</span>
                    <span className="font-medium text-gray-900">{component.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xl font-bold text-gray-900">
                      {component.score}
                    </div>
                    <div className="text-sm text-gray-500">/3</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-indigo-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Note:</strong> Each component is scored from 0 (no difficulty) to 3 (severe difficulty). 
                Scores of 2 or 3 on any component indicate areas that may need attention.
              </p>
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white rounded-2xl p-6 md:p-8 mb-6 shadow-lg">
            <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-indigo-600" />
              Personalized Recommendations
            </h3>

            <div className="space-y-3">
              {severityInfo.recommendations.map((rec, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <p className="text-gray-700">{rec}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => navigate('/booking/find-provider')}
              className="flex items-center justify-center gap-2 bg-indigo-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-md"
            >
              <Calendar className="w-5 h-5" />
              Book Sleep Consultation
            </button>

            <button
              onClick={() => navigate('/self-help')}
              className="flex items-center justify-center gap-2 bg-white text-indigo-600 border-2 border-indigo-600 px-6 py-4 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              More Self-Help Tools
            </button>
          </div>

          {/* Additional Actions */}
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={handleRestart}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Retake Assessment
            </button>

            <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Download className="w-4 h-4" />
              Download Results
            </button>

            <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
              <Share2 className="w-4 h-4" />
              Share with Provider
            </button>
          </div>

          {/* Emergency Support */}
          {scores.global > 15 && (
            <div className="mt-6 bg-red-50 border-2 border-red-200 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-bold text-red-900 mb-2">
                    Immediate Support Available
                  </h4>
                  <p className="text-red-800 mb-4">
                    Your results indicate severe sleep problems. Professional help is strongly recommended.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="tel:+18884047470"
                      className="flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Crisis Helpline: +1 (888) 404-7470
                    </a>
                    <Link
                      to="/emergency"
                      className="flex items-center gap-2 bg-white text-red-600 border-2 border-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Emergency Resources
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Render Question Form
  const currentQuestion = questions[currentStep];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-blue-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header with Progress */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <Link
              to="/self-help"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to Self-Help Tools</span>
              <span className="sm:hidden">Back</span>
            </Link>

            <div className="flex items-center gap-2 text-gray-600">
              <Moon className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-medium">
                Question {currentStep + 1} of {questions.length}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-2">
            <div 
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <div className="text-right text-sm text-gray-600">
            {Math.round(progressPercentage)}% Complete
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10">
          {currentQuestion.component === 'intro' ? (
            // Introduction Screen
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-6">
                <Moon className="w-10 h-10 text-indigo-600" />
              </div>

              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {currentQuestion.title}
              </h1>
              <p className="text-lg text-indigo-600 font-semibold mb-6">
                {currentQuestion.subtitle}
              </p>
              <p className="text-gray-600 mb-8 text-base md:text-lg max-w-2xl mx-auto">
                {currentQuestion.description}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 text-left">
                {currentQuestion.info.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 bg-indigo-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="text-left">
                    <p className="font-semibold text-yellow-900 mb-1">Important Note</p>
                    <p className="text-sm text-yellow-800">
                      This assessment is a screening tool and does not replace professional diagnosis. 
                      If you have concerns about your sleep, please consult a healthcare provider.
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={handleNext}
                className="w-full md:w-auto bg-indigo-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-indigo-700 transition-colors shadow-md flex items-center justify-center gap-2 mx-auto"
              >
                Begin Assessment
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          ) : (
            // Question Screen
            <div>
              <div className="mb-8">
                <div className="inline-block bg-indigo-100 text-indigo-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  {currentQuestion.title}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                  {currentQuestion.question}
                </h2>
              </div>

              <div className="mb-8">
                {currentQuestion.type === 'likert' && (
                  <div className="space-y-3">
                    {currentQuestion.options.map((option) => {
                      const fieldValue = Array.isArray(currentQuestion.field)
                        ? assessmentData[currentQuestion.field[0]][currentQuestion.field[1]]
                        : assessmentData[currentQuestion.field];
                      
                      const isSelected = fieldValue === option.value;

                      return (
                        <button
                          key={option.value}
                          onClick={() => handleInputChange(currentQuestion.field, option.value)}
                          className={`w-full p-4 md:p-5 rounded-xl border-2 text-left transition-all ${
                            isSelected
                              ? 'border-indigo-600 bg-indigo-50 shadow-md'
                              : 'border-gray-200 bg-white hover:border-indigo-300 hover:bg-indigo-50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <CheckCircle className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <span className={`text-base md:text-lg ${
                              isSelected ? 'text-indigo-900 font-semibold' : 'text-gray-700'
                            }`}>
                              {option.label}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {currentQuestion.type === 'number' && (
                  <div>
                    <input
                      type="number"
                      value={assessmentData[currentQuestion.field]}
                      onChange={(e) => handleInputChange(currentQuestion.field, e.target.value)}
                      placeholder={currentQuestion.placeholder}
                      min={currentQuestion.min}
                      max={currentQuestion.max}
                      step={currentQuestion.step || 1}
                      className="w-full p-4 md:p-5 text-lg border-2 border-gray-300 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 transition-all"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      Enter a value between {currentQuestion.min} and {currentQuestion.max}
                    </p>
                  </div>
                )}

                {currentQuestion.type === 'time' && (
                  <div>
                    <input
                      type="time"
                      value={assessmentData[currentQuestion.field]}
                      onChange={(e) => handleInputChange(currentQuestion.field, e.target.value)}
                      className="w-full p-4 md:p-5 text-lg border-2 border-gray-300 rounded-xl focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 transition-all"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      {currentQuestion.placeholder}
                    </p>
                  </div>
                )}
              </div>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t-2 border-gray-100">
                {currentStep > 0 && (
                  <button
                    onClick={handlePrevious}
                    className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Previous
                  </button>
                )}

                <button
                  onClick={handleNext}
                  disabled={!isCurrentQuestionAnswered()}
                  className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
                    isCurrentQuestionAnswered()
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {currentStep === questions.length - 1 ? 'View Results' : 'Next Question'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 mb-3">
            Need help or have questions?
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/faq"
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Visit FAQ
            </Link>
            <span className="text-gray-400">•</span>
            <Link
              to="/contact"
              className="text-indigo-600 hover:text-indigo-800 font-medium"
            >
              Contact Support
            </Link>
            <span className="text-gray-400">•</span>
            <Link
              to="/emergency"
              className="text-red-600 hover:text-red-800 font-medium"
            >
              Emergency Resources
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SleepAssessment;